<template>
  <div class="page-content">
    <div class="payment">
      <div class="payment-header">
        <CustomIcon name="logo" class="payment-header-logo" />
        <h2 class="payment-title">
          Start your virtual journey to a healthier reality
        </h2>
      </div>
      <h3 class="payment-subtitle">Your program</h3>
      <ProgramSummary
        v-for="(phobia, index) in phobias"
        :key="`:id-${phobia.id}-${index}`"
        :title="phobia.phobia"
        :description="phobia.tagline"
        :icon="phobia.image"
      />
      <div class="icon-text-set">
        <IconText iconName="vr" text="Personalized VR exposure therapy plan" />
        <IconText
          iconName="reports"
          text="Develop skills through clinical psychoeducation"
        />
        <IconText
          v-if="onboardingData.choose_path === 'parent'"
          iconName="program"
          text="Track your real world progress"
        />
      </div>

      <div
        class="payment-headset-required"
        v-if="this.$route.query.referrer !== 'allianz'"
      >
        <div class="payment-headset-required-text">
          <h3>This program requires a smartphone VR headset</h3>
          <p>Order ours or source your own</p>
        </div>
        <div class="payment-headset-required-image">
          <img
            :src="require('@/assets/images/ovrcome_vr_headset.png')"
            name="ovrcome-headset"
            alt="ovrcome-headset-image"
          />
        </div>
      </div>

      <LogoBanner :listOfIcons="featuredIcons" text="As featured in" />

      <!-- plan -->
      <div v-if="this.$route.query.referrer === 'allianz'">
        <h3 class="payment-subtitle">You get</h3>
        <div class="payment-select-box-set">
          <SelectBox
            key="plan-1"
            :text="{
              period: '3-Month Access',
              amount: '',
              details: '',
              amount_details: '',
              pay_today: 'Full access for 3 months',
              has_headset: true,
              headset_price: 0,
            }"
            :onClick="() => {}"
            :isActive="true"
          />
        </div>
      </div>
      <div v-else>
        <h3 class="payment-subtitle">Select a plan</h3>
        <div class="payment-select-box-set">
          <SelectBox
            v-for="(plan, index) in currentPlans"
            :key="`:id-${plan.id}-${index}`"
            :text="formatPlanText(plan)"
            :onClick="() => setSelectedPlan(plan)"
            :isActive="Number(formData.plan_id.value) === plan.id"
          />
        </div>

        <!-- headset -->
        <Checkbox
          class="payment-checkbox-button"
          :onClick="(checked) => updateHeadsetSelection(!checked)"
          :isActive="headsetNotNeededActive"
          label="I'll use my own VR headset for smartphones"
          id="own-headset"
        />
      </div>

      <div class="warning small">
        <div class="icon"></div>
        <div>Not available on Meta Quest or Pico devices.</div>
      </div>
      <p class="payment-text">
        Is my smartphone compatible?
        <a :href="deviceSearch" target="_blank">View compatibility.</a>
      </p>

      <p class="payment-text">
        {{ paymentText }}
      </p>
      <SnackbarPrompt v-if="errorMessage" icon="warning" variation="warning">{{
        errorMessage
      }}</SnackbarPrompt>
      <CustomButton
        :disabled="!this.formIsValid()"
        :onClick="() => action(formData.email.value)"
      >
        <span v-if="!registerUserIsLoading">{{ nextButtonText }}</span>
        <LoadingIndicator v-else />
      </CustomButton>
      <p class="payment-text">
        By continuing, I agree to oVRcome’s
        <a :href="terms" target="_blank">Terms & Conditions</a> and
        <a :href="privacyPolicy" target="_blank">Privacy Policy</a>
      </p>
    </div>
    <div class="faq">
      <h3 class="payment-subtitle">FAQ's</h3>
      <div :class="getFaqItemClass('listItem1')">
        <div
          @click="(e) => handleTouchItem(e, 'listItem1')"
          class="faq-list-item-row"
        >
          Does the app work on all smartphones?
          <CustomIcon name="arrow" />
        </div>
        <p class="faq-list-item-slot">
          To confirm if your device is compatible with the oVRcome app, you can
          use this quick look-up tool:
          <a :href="deviceSearch" target="_blank">{{ deviceSearch }}</a>
        </p>
      </div>
      <div :class="getFaqItemClass('listItem2')">
        <div
          @click="(e) => handleTouchItem(e, 'listItem2')"
          class="faq-list-item-row"
        >
          Can I use VR headset with optical glasses?
          <CustomIcon name="arrow" />
        </div>
        <p class="faq-list-item-slot">
          Our VR headset can be worn by people who wear spectacles. It has focus
          adjustment and interpupillary distance wheels which can be adjusted
          for maximum visual comfort and to give you the best immersive virtual
          reality experience possible through a mobile VR headset. If you are
          shortsighted, however, try watching VR with and without reading
          glasses to see what works best. Even though it may feel like you are
          looking at a screen two inches away from your eyes, the actual image
          you see in the VR headset has a focal distance of about 6.5 ft/ 2 m in
          most modern VR headsets.
        </p>
      </div>
      <div :class="getFaqItemClass('listItem3')">
        <div
          @click="(e) => handleTouchItem(e, 'listItem3')"
          class="faq-list-item-row"
        >
          Will I get dizzy using the headset?
          <CustomIcon name="arrow" />
        </div>
        <p class="faq-list-item-slot">
          Extended use of VR can lead to virtual reality sickness which causes
          dizziness, disorientation, nausea, eye soreness, and trouble focusing.
          Please take a 10 to 15 minute break every half hour by taking off your
          headset and looking at objects far away to reduce eye strain, even if
          you don’t think you need it. If symptoms persist, do not try to “power
          through” the content. Stop using the headset and try again in 24
          hours.
        </p>
      </div>
      <div :class="getFaqItemClass('listItem4')">
        <div
          @click="(e) => handleTouchItem(e, 'listItem4')"
          class="faq-list-item-row"
        >
          Does the app work with the Oculus headset (or similar)?
          <CustomIcon name="arrow" />
        </div>
        <p class="faq-list-item-slot">
          While Oculus or Pico style headsets are not compatible with the
          oVRcome app yet, we are working on introducing this integration
          further down the line. We started off by making sure that the
          entry-level to access this product is as low as possible and no
          advanced equipment is required, but understand that some of our users
          already own a VR headset and would like to be able to use it. For now,
          the app is only compatible with a Google Cardboard-style headset (free
          with every subscription, but shipping costs may apply depending on the
          location).
        </p>
      </div>
    </div>
  </div>
</template>
<script>
import CustomIcon from "@/components/CustomIcon.vue";
import SelectBox from "@/components/SelectBox.vue";
import LogoBanner from "@/components/LogoBanner.vue";
import CustomButton from "@/components/CustomButton.vue";
import ProgramSummary from "@/components/ProgramSummary.vue";
import SnackbarPrompt from "@/components/SnackbarPrompt.vue";
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import IconText from "@/components/IconText.vue";
import Checkbox from "@/components/Checkbox.vue";
import { mapGetters } from "vuex";
import {
  MODULES,
  PAYMENT_FORMS,
  EXTERNAL_ROUTES,
  PLAN_TYPES,
  PLAN_BILLING_UNITS,
  PAGE_PATH,
} from "@/config";
import { getCategoryById } from "@/utils";
import { getPlansParams } from "@/utils/get-plans-params";
import { webPaymentPlanValidator } from "@/utils/form-validation/index";
import { pageInformationMixin } from "@/mixins/index.js";
import classnames from "classnames";
import * as queryString from "query-string";

export default {
  data() {
    return {
      phobias: [],
      planType: PLAN_TYPES.globalPlan,
      featuredIcons: ["orgs/1-news", "orgs/news-hub", "orgs/uoo"],
      validator: webPaymentPlanValidator,
      terms: EXTERNAL_ROUTES.termsAndConditions,
      privacyPolicy: EXTERNAL_ROUTES.privacyPolicy,
      deviceSearch: EXTERNAL_ROUTES.deviceSearch,
      registerUserIsLoading: false,
      errorMessage: null,
      touched: {
        listItem1: false,
        listItem2: false,
        listItem3: false,
        listItem4: false,
      },
      bespokeReferrer: false,
      d2c_vaping_plan: false,
      nextButtonText: "Next - Payment",
      paymentText:
        "Prices in USD. Free shipping to the USA. See shipping costs to other locations on the next page.",
      noHeadset: false,
      currentPlans: [],
      headsetNeededActive: true,
      headsetNotNeededActive: false,
      currentPlanIndex: 0,
    };
  },
  props: {
    updateFormState: Function,
    action: Function,
    formData: Object,
  },
  components: {
    CustomIcon,
    SelectBox,
    LogoBanner,
    CustomButton,
    ProgramSummary,
    SnackbarPrompt,
    LoadingIndicator,
    IconText,
    Checkbox,
  },
  mixins: [pageInformationMixin],
  mounted() {
    const { dispatch } = this.$store;
    const { onboardingData, formData, availablePlans } = this;

    if (onboardingData.choose_path === PAGE_PATH.parent) {
      this.phobias = onboardingData.choose_phobias.map((id) =>
        getCategoryById(id)
      );
    } else {
      this.phobias = [getCategoryById(onboardingData.choose_phobias)];
      this.setInitialSearchParams();
    }

    this.currentPlans = this.getPlans();

    if (availablePlans.length < 1) {
      dispatch(
        `${MODULES.PLAN}/getPlansAction`,
        getPlansParams({ onboardingData })
      ).then(() => {
        if (!formData.plan_id.value) {
          this.updateFormState(
            PAYMENT_FORMS.web,
            "plan_id",
            this.getPlans()[0]?.id,
            false
          );
        }
      });
    }

    this.bespokeReferrer =
      this.$route.query.referrer === "allianz" ||
      this.$route.query.referrer === "ease_up" ||
      this.$route.query.referrer === "moe_vaping";

    if ([1012, 1013].includes(onboardingData.choose_phobias)) {
      if (this.bespokeReferrer) {
        //if it's the vaping programme or the allianz driving program, then make sure monthly plan is selected by default
        this.updateFormState(
          PAYMENT_FORMS.web,
          "plan_id",
          availablePlans[1]?.id,
          false
        );
      } else {
        //if it's the non-bespoke referrer, d2c vaping plan, then make sure yearly plan is selected by default
        this.updateFormState(
          PAYMENT_FORMS.web,
          "plan_id",
          availablePlans[0]?.id,
          false
        );
      }
    }

    this.d2c_vaping_plan = this.$route.query.referrer === "d2c_vaping";
    if (this.bespokeReferrer) {
      this.nextButtonText = "Next - Shipping";
      this.paymentText = "";
    }
    if (this.d2c_vaping_plan) {
      this.paymentText =
        "Prices in USD. Free shipping to the USA. See shipping costs to other locations on the next page. 1 USD = 1.60 NZD";
    }
    if (this.$route.query.referrer === "ease_up") {
      this.nextButtonText = "Next - Subscription";
    }
    if (this.$route.query.referrer === "moe_vaping") {
      this.nextButtonText = "Next";
    }

    dispatch(`${MODULES.COUNTRY}/getCountriesAction`);

    this.updateHeadsetSelection(this.headsetNeededActive);
  },
  computed: {
    ...mapGetters(MODULES.WELCOME, {
      onboardingData: "getOnboardingData",
    }),
    ...mapGetters(MODULES.PLAN, {
      availablePlans: "getAvailablePlans",
    }),
  },
  methods: {
    setInitialSearchParams() {
      const search = location.hash.replace("#/", "");
      const whiteListedKeys = ["referrer"];

      if (search) {
        const obj = queryString.parse(search);

        Object.entries(obj)
          .filter((item) => whiteListedKeys.includes(item[0]))
          .forEach((entry) =>
            this.updateFormState(PAYMENT_FORMS.web, entry[0], entry[1], false)
          );
      }
    },
    getFaqItemClass(id) {
      const className = "faq-list-item";

      return classnames(className, {
        ["is-active"]: this.touched[id],
      });
    },
    handleTouchItem(e, id) {
      if (e) {
        e.preventDefault();
      }

      this.touched[id] = !this.touched[id];
    },
    getPlans() {
      const { onboardingData } = this;
      if (onboardingData.choose_path === PAGE_PATH.myself) {
        if ([1012, 1013].includes(onboardingData.choose_phobias)) {
          if (this.bespokeReferrer) {
            //if we're selecting the vaping programme or the allianz driving program
            return this.availablePlans.filter(
              (plan) =>
                plan.billing_units === "monthly" && plan.included_product //only show monthly plan
            );
          } else {
            //if we're on a non-bespoke referrer, d2c vaping plan
            return this.availablePlans.filter(
              (plan) => plan.billing_units === "yearly" && plan.included_product //only show monthly plan
            );
          }
        } else {
          if (this.noHeadset) {
            return this.availablePlans.filter(
              (plan) =>
                plan.type === PLAN_TYPES.globalPlan && !plan.included_product_id
            );
          } else {
            return this.availablePlans.filter(
              (plan) =>
                plan.type === PLAN_TYPES.globalPlan && plan.included_product_id
            );
          }
        }
      }

      if (onboardingData.choose_path === PAGE_PATH.parent) {
        if (this.noHeadset) {
          return this.availablePlans.filter(
            (plan) => !plan.included_product_id
          );
        } else {
          return this.availablePlans.filter((plan) => plan.included_product_id);
        }
      }
    },
    updateHeadsetSelection(headsetSelected) {
      this.noHeadset = !headsetSelected;
      this.headsetNeededActive = headsetSelected;
      this.headsetNotNeededActive = !headsetSelected;
      this.currentPlans = this.getPlans();
      var headsetType = "regular";
      if (!headsetSelected) {
        headsetType = "none";
      }
      this.updateFormState(PAYMENT_FORMS.web, "headsetType", headsetType, true);

      //when the plans are updated, make sure the first plan is selected
      if (this.availablePlans.length >= 1) {
        this.updateFormState(
          PAYMENT_FORMS.web,
          "plan_id",
          this.getPlans()[this.currentPlanIndex]?.id,
          false
        );
      }
    },
    calculateHeadsetPrice(plan) {
      if (!plan || !plan.included_product) {
        return 0;
      }
      // headset is free
      else if (plan.first_month_price.amount == plan.price.amount) {
        return 0;
      }
      return Number(plan.included_product.price.amount);
    },
    setSelectedPlan(plan) {
      if (plan.billing_units === PLAN_BILLING_UNITS.yearly) {
        this.currentPlanIndex = 0;
      } else {
        this.currentPlanIndex = 1;
      }
      this.updateFormState("webForm", "plan_id", plan.id, true);
    },
    formatPlanText(plan) {
      let period;
      let amount;
      let details;
      let amount_details;
      let pay_today;
      let headset_price;
      let has_headset = false;

      period =
        plan.billing_units === PLAN_BILLING_UNITS.yearly ? "Annual" : "Monthly";

      let singular_period = plan.billing_units.replace("ly", "");

      // flat rate plan
      if (
        !plan.first_month_price ||
        plan.price.amount == plan.first_month_price.amount
      ) {
        amount = `US$${Number(plan.price.amount)}`;
        amount_details = `/ ${singular_period}`;
        pay_today = `US$${Number(plan.price.amount)}`;
      }

      // first month price == headset price
      // first month free
      else if (
        plan.first_month_price.amount == 0 ||
        (plan.included_product &&
          plan.first_month_price.amount == plan.included_product.price.amount)
      ) {
        amount = `First ${singular_period} free!`;
        amount_details = `Then US$${Number(
          plan.price.amount
        )} / ${singular_period}`;
        pay_today =
          plan.first_month_price.amount == 0
            ? "No payment required!"
            : `US$${Number(plan.first_month_price.amount)}`;
      }

      // discounted first month
      else {
        amount = `US$${Number(plan.first_month_price.amount)}`;
        amount_details = `Then US$${Number(
          plan.price.amount
        )} / ${singular_period}`;
        pay_today = `US$${Number(plan.first_month_price.amount)}`;
      }

      // headset included?
      if (plan.included_product) {
        pay_today += " + shipping";
        has_headset = true;
        headset_price = this.calculateHeadsetPrice(plan);
      }

      // recurring
      if (plan.is_recurring) {
        details = "Cancel anytime";
      }
      // not recurring
      else {
        details = "No trial, payment upfront";
      }

      return {
        period,
        amount,
        details,
        amount_details,
        pay_today,
        has_headset,
        headset_price,
      };
    },
    formIsValid() {
      const keys = Object.keys(this.formData);

      const properties = (validator, key) =>
        Object.prototype.hasOwnProperty.call(validator, key);

      const formIsValid = !keys.some((key) => {
        if (properties(this.validator, key)) {
          return !this.validator[key](this.formData[key].value).valid;
        }

        return false;
      });

      if (!this.headsetNeededActive && !this.headsetNotNeededActive) {
        return false;
      }

      return formIsValid;
    },
  },
};
</script>
